import { useState, useEffect, useCallback } from 'react';
import { supabase, useAuth } from '@mondivago/supabase';
import { useSupplierProfile } from '@/hooks/suppliers';
import { BookingRecord, EnrichedBookingRecord, EnrichedTourRow, BookingAddonRow } from '@mondivago/types';
import { logger } from '@/shared/lib/logger';

interface TourLookup {
  id: string;
  title: string;
  photos: string[] | null;
  city: string;
}

/**
 * Hook to manage bookings for the current supplier.
 * Handles fetching enriched booking data and performing updates.
 */
export function useBookings() {
  const { user, agencyId } = useAuth();
  const { profile, agency } = useSupplierProfile();
  const supplierId = profile?.id ?? agency?.id ?? null;

  const [bookings, setBookings] = useState<EnrichedBookingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBookings = useCallback(async () => {
    if (!user || !supplierId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Fetch Enriched Bookings via RPC (Fat Backend)
      // If user is a worker with a parent supplier, fetch all parent supplier bookings
      const hasParentSupplier = !!agencyId;

      const { data, error: dbError } = await supabase.rpc('get_supplier_bookings_enriched', {
        p_supplier_id: hasParentSupplier ? null : supplierId,
        p_agency_id: hasParentSupplier ? agencyId : null,
      });

      if (dbError) throw dbError;

      const rawBookings = (data || []) as EnrichedBookingRecord[];

      // 2. Augment with Tour Details (Missing from RPC)
      const tourIds = [...new Set(rawBookings.map((b) => b.tour_id))].filter(Boolean);

      const toursMap: Record<string, TourLookup> = {};

      if (tourIds.length > 0) {
        const { data: toursData } = await supabase
          .from('tours')
          .select('id, title, photos, content_cities(name)')
          .in('id', tourIds);

        if (toursData) {
          toursData.forEach((t) => {
            toursMap[t.id] = {
              id: t.id,
              title: t.title,
              photos: t.photos,
              city: t.content_cities?.name || 'Mondivago',
            };
          });
        }
      }

      // 3. Augment with Addons
      const bookingIds = rawBookings.map((b) => b.id);
      const addonsMap: Record<string, BookingAddonRow[]> = {};

      if (bookingIds.length > 0) {
        const { data: addonsData } = await supabase.from('booking_addons').select('*').in('booking_id', bookingIds);

        if (addonsData) {
          addonsData.forEach((a) => {
            if (!addonsMap[a.booking_id]) addonsMap[a.booking_id] = [];
            addonsMap[a.booking_id].push(a);
          });
        }
      }

      // 4. Augment with Traveler Phones
      const travelerIds = [...new Set(rawBookings.map((b) => b.traveler_id))].filter(Boolean);
      const phonesMap: Record<string, string | null> = {};

      if (travelerIds.length > 0) {
        const { data: travelersData } = await supabase
          .from('travelers')
          .select('user_id, phone')
          .in('user_id', travelerIds);

        if (travelersData) {
          travelersData.forEach((t) => {
            if (t.phone) phonesMap[t.user_id] = t.phone;
          });
        }
      }

      // 5. Augment with Partner data (partner_id & partner_agent_id not returned by RPC)
      const bookingIdsSet = rawBookings.map((b) => b.id);
      const partnerIds = new Set<string>();
      const partnerAgentIds = new Set<string>();
      const bookingPartnerMap: Record<string, { partner_id: string | null; partner_agent_id: string | null }> = {};

      if (bookingIdsSet.length > 0) {
        const { data: partnerRefs } = await supabase
          .from('bookings')
          .select('id, partner_id, partner_agent_id')
          .in('id', bookingIdsSet);

        if (partnerRefs) {
          partnerRefs.forEach((b) => {
            bookingPartnerMap[b.id] = { partner_id: b.partner_id, partner_agent_id: b.partner_agent_id };
            if (b.partner_id) partnerIds.add(b.partner_id);
            if (b.partner_agent_id) partnerAgentIds.add(b.partner_agent_id);
          });
        }
      }

      // Fetch partner company names
      const partnersMap: Record<string, { id: string; company_name: string }> = {};
      if (partnerIds.size > 0) {
        const { data: partnersData } = await supabase
          .from('partners')
          .select('id, company_name')
          .in('id', [...partnerIds]);

        if (partnersData) {
          partnersData.forEach((p) => {
            partnersMap[p.id] = p;
          });
        }
      }

      // Fetch partner agent names
      const partnerAgentsMap: Record<string, { id: string; first_name: string; last_name: string }> = {};
      if (partnerAgentIds.size > 0) {
        const { data: agentsData } = await supabase
          .from('partner_workers')
          .select('id, first_name, last_name')
          .in('id', [...partnerAgentIds]);

        if (agentsData) {
          agentsData.forEach((a) => {
            partnerAgentsMap[a.id] = a;
          });
        }
      }

      const enriched = rawBookings.map((b) => {
        const partnerRef = bookingPartnerMap[b.id];
        const partner = partnerRef?.partner_id ? partnersMap[partnerRef.partner_id] : null;
        const partnerAgent = partnerRef?.partner_agent_id ? partnerAgentsMap[partnerRef.partner_agent_id] : null;

        return {
          ...b,
          partner_id: partnerRef?.partner_id ?? null,
          partner_agent_id: partnerRef?.partner_agent_id ?? null,
          partners: partner ? { id: partner.id, company_name: partner.company_name } : null,
          partner_workers: partnerAgent
            ? { id: partnerAgent.id, first_name: partnerAgent.first_name, last_name: partnerAgent.last_name }
            : null,
          tours: (toursMap[b.tour_id] as unknown as EnrichedTourRow) || null,
          booking_addons: addonsMap[b.id] || [],
          traveler_phone:
            phonesMap[b.traveler_id] ||
            (b as EnrichedBookingRecord & { traveler_phone: string | null }).traveler_phone ||
            ((b.metadata as Record<string, unknown> | null)?.traveler_phone as string | undefined) ||
            null,
        };
      }) as EnrichedBookingRecord[];

      const sorted = enriched.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setBookings(sorted);
    } catch (err) {
      logger.error('Error fetching bookings:', err);
      setError('Error loading bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, supplierId, profile]);

  /**
   * Updates an existing booking.
   */
  const updateBooking = useCallback(async (id: string, updates: Partial<BookingRecord>) => {
    setError(null);
    try {
      const { error: dbError } = await supabase.from('bookings').update(updates).eq('id', id);

      if (dbError) throw dbError;

      // Optimistic update
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
      return { success: true };
    } catch (err) {
      const msg = 'Error updating booking. Please try again.';
      setError(msg);
      return { success: false, error: msg };
    }
  }, []);

  /**
   * Cancels a booking and refunds the FULL amount (policies disabled for now).
   * Always routes through the refund edge function so a real Stripe refund is issued.
   */
  const cancelBooking = useCallback(async (id: string, reason?: string) => {
    setError(null);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const { data, error: fnError } = await supabase.functions.invoke('api/booking/refund', {
        body: { bookingId: id, reason: reason || 'Cancelled by supplier' },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      // Optimistic update
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: 'cancelled' as const } : b)));
      return { success: true };
    } catch (err) {
      const msg = 'Error cancelling booking. Please try again.';
      setError(msg);
      return { success: false, error: msg };
    }
  }, []);

  useEffect(() => {
    void fetchBookings();
  }, [fetchBookings]);

  return {
    bookings,
    loading,
    error,
    refresh: fetchBookings,
    updateBooking,
    cancelBooking,
    supplierId: supplierId,
  };
}
